
import React, { Component } from "react";
import { reduxForm, Field, reset, formValueSelector } from "redux-form";
import { required, renderField } from "../../../support/forms";
import { renderSelectList, renderSelectIdNameField } from "../../../support/forms";
import { FinalFormCheckbox, FinalFormTextField } from "../../../common/formWrappers";
import { unassignDirectorAdvisor, setDirector, getAdvisorBasics, getPotentialAdvisorSubordinates, assignAdvisorSchool, unassignAdvisorSchool, assignAdvisorGrade, unassignAdvisorGrade, assignAdvisorZipCode, unassignAdvisorZipCode, assignDirectorAdvisor, setGap, setHonorific, setAccelerate, getAdvisorGoals, setAdvisorGoal, removeAdvisorGoal, setDepHighSchool, setDepMiddleSchool, setDepFamilyEngagement, setDepPostSecondary, getObjectives, addAdvisorGoal, updateAdvisorGoal, deleteAdvisorGoal, returnAdvisorBasicsAsync, getCode, assignAdvisorInstitution, unassignAdvisorInstitution, getActiveSpecialGroups, getAdvisorSpecialGroups, assignAdvisorSpecialGroup, unassignAdvisorSpecialGroup, getAllSegments, getAdvisorSegments, assignSegmentToAdvisor, unassignSegmentFromAdvisor } from "./apis";
import Multiselect from 'multiselect-react-dropdown';
import Checkbox from "@material-ui/core/Checkbox";
import { FormControlLabel } from '@mui/material';
import AdvisorGoalsManager from "./advisorgoalsform";
import SimulateAssignmentModal from "./SimulateAssignment";
import CoverageAnalysisModal from "./CoverageAnalysis";
import { SecurityContext } from "../../../support/security";
import * as taggingApi from "../../../api/tagging";
import { getAllDistricts } from "../schools/apis";
import { toast } from "react-toastify";
import "./edit.css";

// Grades 5-18 matching the advisor caseload filter (see advisors/context grade list).
const gradeOptions = Array.from({ length: 14 }, (_, i) => {
    const grade = i + 5;
    return { name: `${grade}`, grade };
});

// Mirror the server-side normalization of UserProfile.ZipCode: strip dashes/spaces and truncate
// ZIP+4 input down to the first 5 digits. Returns "" when no 5-digit zip can be derived so the
// caller can surface a validation message.
const normalizeZipCode = (value) => {
    const digits = (value || "").replace(/\D/g, "");
    return digits.length >= 5 ? digits.substring(0, 5) : "";
};

class Advisor extends Component {

    // OWL-2293: gate the simulation "Apply" (commit) on the current user's Advisor.Manage.edit permission so
    // only Admins can apply; directors (Advisor.Manage.view) can still open the modal and run simulations.
    static contextType = SecurityContext;

    state = {
        advisorInfo: [],
        potSubords: [],
        schools: [],
        districts: [],
        selectedDistricts: [],
        schoolSelectorKey: 0,
        grades: [],
        zipCodes: [],
        zipInput: "",
        zipError: "",
        zipAdding: false,
        zipRemoving: [],
        institutions: [],
        subs: [],
        showSubs: false,
        goals: [],
        goalsList: [],
        taggingOptions: [],
        isGAP: false,
        isHonorific: false,
        isAccelerate: false,
        activeSpecialGroups: [],
        selectedSpecialGroups: [],
        specialGroupsKey: 0,
        // OWL-2292: saved segments the advisor is linked to (attach = its matching students flow into the caseload).
        segmentOptions: [],
        selectedSegments: [],
        segmentsKey: 0,
        depFamilyEngagement: false,
        depMiddleSchool: false,
        depHighSchool: false,
        depPostSecondary: false,
        disabled: true,
        availableObj: [],
        objTags: [],
        showSimulate: false,
        showCoverage: false,
        loaded: false
    };

    componentDidMount = async () => {
        await this.loadData();
    }

    // OWL-2293: true only for Admins (Advisor.Manage.edit). Directors (Advisor.Manage.view) may run the
    // simulation but cannot apply it. The context is provided by the app-wide SecurityProvider.
    canApplyAssignment = () => {
        return !!(this.context && this.context.hasEditPermission && this.context.hasEditPermission("Advisor.Manage"));
    };

    // OWL-2293: re-usable loader so the assignment simulator can refresh the form's criteria after Apply.
    loadData = async () => {
        const input = { id: this.props.userId };

        const adInf = await returnAdvisorBasicsAsync(input);
        const potSub = await getPotentialAdvisorSubordinates(adInf.data.result.userProfileId);
        const goals = await getAdvisorGoals(adInf.data.result.userProfileId);
        const taggingOptions = await taggingApi.getActiveTags();
        const obj = await getObjectives();

        const activeGroupsResp = await getActiveSpecialGroups();
        const activeSpecialGroups = activeGroupsResp.data.result || [];

        const districtsResp = await getAllDistricts();
        const districts = districtsResp.data.result || [];
        const assignedGroupsResp = await getAdvisorSpecialGroups(adInf.data.result.userProfileId);
        // Assigned groups come back as full DTOs (including groups that are now inactive) so lingering
        // historical assignments still render as removable chips even though they aren't selectable options.
        const selectedSpecialGroups = assignedGroupsResp.data.result || [];

        // OWL-2292: segment picker options (all saved segments) + the advisor's current segment links.
        // Best-effort: users without segment permission simply get no segment data rather than a broken form.
        let segmentOptions = [];
        let selectedSegments = [];
        try {
            const allSegmentsResp = await getAllSegments();
            segmentOptions = (allSegmentsResp.data.result || []).map((s) => ({ id: s.id, name: s.name }));
            const advisorSegmentsResp = await getAdvisorSegments(adInf.data.result.userProfileId);
            selectedSegments = (advisorSegmentsResp.data.result || []).map((a) => ({ id: a.segmentId, name: a.segmentName }));
        } catch (e) {
            segmentOptions = [];
            selectedSegments = [];
        }

        const inf = adInf.data.result;
        const sup = adInf.data.result.isSupervisor;
        const toss = taggingOptions.data.result.filter(flt => flt.classification.advisorGoals == true);
        const objTags = taggingOptions.data.result.filter(flt => flt.classification.middleSchoolGoals == true || flt.classification.highSchoolGoals == true || flt.classification.postsecondaryGoals == true || flt.classification.familyEngagementGoals == true || flt.classification.gapGoals == true || flt.classification.honorificGoals == true || flt.classification.postsecondaryGoals == true || flt.classification.familyEngagementGoals == true || flt.classification.gapGoals == true || flt.classification.accelerateGoals == true);


        let tl = [];
        if (adInf.data.result.isStaff || adInf.data.result.isAdmin) {
            sup = false;
        }
        for (const tos of toss) {

            tl.push({ color: tos.classification.color, id: tos.id, label: tos.name });


        }

        const advGoals = goals.data.result;


        let schools = [];
        let grades = [];
        let zipCodes = [];
        let institutions = [];
        let subs = [];
        if (adInf.data.result.grades != null) {
            adInf.data.result.grades.forEach((gr) => {
                const grInfo = gradeOptions.filter((opt) => opt.grade == gr.grade)[0];
                if (grInfo) {
                    grades.push(grInfo);
                }
            });
        }
        if (adInf.data.result.zipCodes != null) {
            adInf.data.result.zipCodes.forEach((zc) => {
                if (zc.zipCode) {
                    zipCodes.push(zc.zipCode);
                }
            });
        }
        if (adInf.data.result.schools != null) {
            adInf.data.result.schools.forEach((sch) => {

                let schInfo = this.props.schools.filter((school) => school.id == sch.schoolId)[0];

                schools.push(schInfo);
            });

        }
        if (adInf.data.result.institutions != null) {
            adInf.data.result.institutions.forEach((inst) => {

                let schInfo = this.props.institutions.filter((institution) => institution.id == inst.institutionId)[0];

                institutions.push(schInfo);
            });

        }

        const potSubs = potSub.data.result;

        if (adInf.data.result.subordinates != null && !adInf.data.result.isStaff && !adInf.data.result.isAdmin) {

            adInf.data.result.subordinates.forEach((sub) => {


                const subInfo = potSubs.filter((sb) => sb.userProfileId == sub.userProfileId);

                subs.push(subInfo[0]);
            });

        }


        this.setState({
            advisorInfo: inf,
            isGAP: inf.isGAP,
            isHonorific: inf.isHonorific,
            isAccelerate: inf.isAccelerate,
            activeSpecialGroups: activeSpecialGroups,
            selectedSpecialGroups: selectedSpecialGroups,
            segmentOptions: segmentOptions,
            selectedSegments: selectedSegments,
            depFamilyEngagement: inf.depFamilyEngagement,
            depMiddleSchool: inf.depMiddleSchool,
            depHighSchool: inf.depHighSchool,
            depPostSecondary: inf.depPostSecondary,
            potSubords: potSubs,
            showSubs: sup,
            schools: schools,
            districts: districts,
            grades: grades,
            zipCodes: zipCodes,
            subs: subs,
            goals: advGoals,
            goalsList: tl,
            taggingOptions: taggingOptions.data.result,
            disabled: false, //this.props.readonly,
            availableObj: obj.data.result,
            objTags: objTags,
            loaded: true,
            institutions: institutions
        });
    }
    setStateAsync(state) {
        return new Promise((resolve) => {
            this.setState(state, resolve);
        });
    }

    onSchoolSelect = async (selectedList, selectedItem) => {

        const data = { schoolId: selectedItem.id, userProfileId: this.state.advisorInfo.userProfileId };
        await assignAdvisorSchool(data);
        // Keep state.schools in sync so a later programmatic remount (district populate) preserves manual selections.
        this.setState({ schools: selectedList });
    }
    onSchoolRemove = async (selectedList, removedItem) => {


        const data = { schoolId: removedItem.id, userProfileId: this.state.advisorInfo.userProfileId };
        await unassignAdvisorSchool(data);
        this.setState({ schools: selectedList });
    }

    onDistrictSelect = async (selectedList, selectedItem) => {
        this.setState({ disabled: true });
        try {
            const districtSchools = this.props.schools.filter((school) => school.districtId === selectedItem.id);
            const newSchools = districtSchools.filter(
                (school) => !this.state.schools.some((assigned) => assigned.id === school.id)
            );

            await Promise.all(
                newSchools.map((school) =>
                    assignAdvisorSchool({ schoolId: school.id, userProfileId: this.state.advisorInfo.userProfileId })
                )
            );

            this.setState((prevState) => ({
                schools: [...prevState.schools, ...newSchools],
                selectedDistricts: selectedList,
                schoolSelectorKey: prevState.schoolSelectorKey + 1,
            }));
        } finally {
            this.setState({ disabled: false });
        }
    }

    onDistrictRemove = async (selectedList) => {
        // Deselecting a district is purely a UI action; schools stay assigned (assignment is school-level).
        this.setState({ selectedDistricts: selectedList });
    }

    onGradeSelect = async (selectedList, selectedItem) => {
        const data = { grade: selectedItem.grade, userProfileId: this.state.advisorInfo.userProfileId };
        await assignAdvisorGrade(data);
        this.setState({ grades: selectedList });
    }
    onGradeRemove = async (selectedList, removedItem) => {
        const data = { grade: removedItem.grade, userProfileId: this.state.advisorInfo.userProfileId };
        await unassignAdvisorGrade(data);
        this.setState({ grades: selectedList });
    }

    onZipInputChange = (e) => {
        this.setState({ zipInput: e.target.value, zipError: "" });
    }

    onZipKeyDown = (e) => {
        // Add the zip on Enter so the input behaves like a tag field.
        if (e.key === "Enter") {
            e.preventDefault();
            this.onZipAdd();
        }
    }

    onZipAdd = async () => {
        if (this.state.zipAdding) {
            return;
        }
        const zip = normalizeZipCode(this.state.zipInput);
        if (!zip) {
            this.setState({ zipError: "Enter a valid 5-digit zip code." });
            return;
        }
        if (this.state.zipCodes.includes(zip)) {
            this.setState({ zipInput: "", zipError: "" });
            return;
        }

        const data = { zipCode: zip, userProfileId: this.state.advisorInfo.userProfileId };
        this.setState({ zipAdding: true, zipError: "" });
        try {
            await assignAdvisorZipCode(data);
            this.setState({ zipCodes: [...this.state.zipCodes, zip], zipInput: "" });
        } catch (err) {
            this.setState({ zipError: "Could not add zip code. Please try again." });
        } finally {
            this.setState({ zipAdding: false });
        }
    }

    onZipRemove = async (zip) => {
        if (this.state.zipRemoving.includes(zip)) {
            return;
        }
        const data = { zipCode: zip, userProfileId: this.state.advisorInfo.userProfileId };
        this.setState({ zipRemoving: [...this.state.zipRemoving, zip], zipError: "" });
        try {
            await unassignAdvisorZipCode(data);
            this.setState({ zipCodes: this.state.zipCodes.filter((z) => z !== zip) });
        } catch (err) {
            this.setState({ zipError: "Could not remove zip code. Please try again." });
        } finally {
            this.setState({ zipRemoving: this.state.zipRemoving.filter((z) => z !== zip) });
        }
    }

    directorChanged = async () => {
        let subs = [];
        const data = { userProfileId: this.state.advisorInfo.userProfileId, IsSupervisor: !this.state.showSubs };
        await setDirector(data);
        const adInf = await getAdvisorBasics(this.props.userId);

        adInf.data.result.subordinates.forEach((sub) => {


            const subInfo = this.state.potSubords.filter((sb) => sb.userProfileId == sub.userProfileId);

            subs.push(subInfo[0]);
        });

        this.setState({ showSubs: !this.state.showSubs, subs: subs, advisorInfo: adInf.data.result });
    }



    onSubSelect = async (selectedList, selectedItem) => {
        const data = { userProfileId: this.state.advisorInfo.userProfileId, subordinateUserProfileId: selectedItem.userProfileId }
        await assignDirectorAdvisor(data);
    }

    onSubRemove = async (selectedList, removedItem) => {
        const data = { userProfileId: this.state.advisorInfo.userProfileId, subordinateUserProfileId: removedItem.userProfileId }
        await unassignDirectorAdvisor(data);

    }


    onObjSelect = async (selectedList, selectedItem) => {
        const data = { AdvisorId: this.state.advisorInfo.userProfileId, TagsId: selectedItem.id, SubtagId: null }
        await setAdvisorGoal(data);
    }

    onObjRemove = async (selectedList, removedItem) => {
        const data = { AdvisorId: this.state.advisorInfo.userProfileId, TagsId: removedItem.id, SubtagId: null }
        await removeAdvisorGoal(data);

    }

    gapChanged = async () => {
        this.setState({ disabled: true }); // Set disabled to true at the start

        await this.setStateAsync(prevState => ({
            isGAP: !prevState.isGAP
        }));
        const data = {
            userProfileId: this.state.advisorInfo.userProfileId,
            IsGAP: this.state.isGAP
        };
        await setGap(data);

        this.setState({ disabled: false }); // Set disabled back to false at the end
    }

    honorificChanged = async () => {
        this.setState({ disabled: true });

        await this.setStateAsync(prevState => ({
            isHonorific: !prevState.isHonorific
        }));
        const data = {
            userProfileId: this.state.advisorInfo.userProfileId,
            isHonorific: this.state.isHonorific
        };
        await setHonorific(data);

        this.setState({ disabled: false });
    }

    accelerateChanged = async () => {
        this.setState({ disabled: true });

        await this.setStateAsync(prevState => ({
            isAccelerate: !prevState.isAccelerate
        }));
        const data = {
            userProfileId: this.state.advisorInfo.userProfileId,
            isAccelerate: this.state.isAccelerate
        };
        await setAccelerate(data);

        this.setState({ disabled: false });
    }

    onSpecialGroupSelect = async (selectedList, selectedItem) => {
        const previous = this.state.selectedSpecialGroups;
        this.setState({ disabled: true });
        try {
            await assignAdvisorSpecialGroup({
                userProfileId: this.state.advisorInfo.userProfileId,
                specialGroupId: selectedItem.id,
            });
            this.setState({ selectedSpecialGroups: selectedList });
        } catch (error) {
            toast.error("Could not assign special group. Please try again.");
            // Revert to the last known-good selection and force the multiselect to remount so the
            // phantom chip is dropped (the widget won't otherwise re-sync from selectedValues).
            this.setState((state) => ({
                selectedSpecialGroups: previous,
                specialGroupsKey: state.specialGroupsKey + 1,
            }));
        } finally {
            this.setState({ disabled: false });
        }
    };

    onSpecialGroupRemove = async (selectedList, removedItem) => {
        const previous = this.state.selectedSpecialGroups;
        this.setState({ disabled: true });
        try {
            await unassignAdvisorSpecialGroup({
                userProfileId: this.state.advisorInfo.userProfileId,
                specialGroupId: removedItem.id,
            });
            this.setState({ selectedSpecialGroups: selectedList });
        } catch (error) {
            toast.error("Could not remove special group. Please try again.");
            // Restore the chip that failed to remove and remount so it reappears correctly.
            this.setState((state) => ({
                selectedSpecialGroups: previous,
                specialGroupsKey: state.specialGroupsKey + 1,
            }));
        } finally {
            this.setState({ disabled: false });
        }
    };

    // OWL-2292: attach a saved segment to this advisor. Every current student matching the segment's
    // criteria then flows into the advisor's caseload (resolved live), except students manually overridden
    // elsewhere. Mirrors the special-group handlers' optimistic-update-with-revert pattern.
    onSegmentSelect = async (selectedList, selectedItem) => {
        const previous = this.state.selectedSegments;
        this.setState({ disabled: true });
        try {
            await assignSegmentToAdvisor({
                userProfileId: this.state.advisorInfo.userProfileId,
                segmentId: selectedItem.id,
            });
            this.setState({ selectedSegments: selectedList });
        } catch (error) {
            toast.error("Could not attach segment. Please try again.");
            this.setState((state) => ({
                selectedSegments: previous,
                segmentsKey: state.segmentsKey + 1,
            }));
        } finally {
            this.setState({ disabled: false });
        }
    };

    onSegmentRemove = async (selectedList, removedItem) => {
        const previous = this.state.selectedSegments;
        this.setState({ disabled: true });
        try {
            await unassignSegmentFromAdvisor({
                userProfileId: this.state.advisorInfo.userProfileId,
                segmentId: removedItem.id,
            });
            this.setState({ selectedSegments: selectedList });
        } catch (error) {
            toast.error("Could not remove segment. Please try again.");
            this.setState((state) => ({
                selectedSegments: previous,
                segmentsKey: state.segmentsKey + 1,
            }));
        } finally {
            this.setState({ disabled: false });
        }
    };

    depFamilyEngagementChanged = async () => {
        this.setState({ disabled: true });

        await this.setStateAsync(prevState => ({
            depFamilyEngagement: !prevState.depFamilyEngagement
        }));
        const data = {
            userProfileId: this.state.advisorInfo.userProfileId,
            depFamilyEngagement: this.state.depFamilyEngagement
        };
        await setDepFamilyEngagement(data);

        this.setState({ disabled: false });
    }

    depMiddleSchoolChanged = async () => {
        this.setState({ disabled: true });

        await this.setStateAsync(prevState => ({
            depMiddleSchool: !prevState.depMiddleSchool
        }));
        const data = {
            userProfileId: this.state.advisorInfo.userProfileId,
            depMiddleSchool: this.state.depMiddleSchool
        };
        await setDepMiddleSchool(data);

        this.setState({ disabled: false });
    }

    depHighSchoolChanged = async () => {
        this.setState({ disabled: true });

        await this.setStateAsync(prevState => ({
            depHighSchool: !prevState.depHighSchool
        }));
        const data = {
            userProfileId: this.state.advisorInfo.userProfileId,
            depHighSchool: this.state.depHighSchool
        };
        //      //console.log(data);
        await setDepHighSchool(data);

        this.setState({ disabled: false });
    }

    depPostSecondaryChanged = async () => {
        this.setState({ disabled: true });

        await this.setStateAsync(prevState => ({
            depPostSecondary: !prevState.depPostSecondary
        }));
        const data = {
            userProfileId: this.state.advisorInfo.userProfileId,
            depPostSecondary: this.state.depPostSecondary
        };
        await setDepPostSecondary(data);

        this.setState({ disabled: false });
    }

    addAdvisorGoal = async (input) => {
        await addAdvisorGoal(input);
        const goals = await getAdvisorGoals(this.state.advisorInfo.userProfileId);
        this.setState({ goals: goals.data.result });
    }

    updateAdvisorGoal = async (input) => {
        await updateAdvisorGoal(input);
        const goals = await getAdvisorGoals(this.state.advisorInfo.userProfileId);
        this.setState({ goals: goals.data.result });
    }


    deleteAdvisorGoal = async (input) => {
        await deleteAdvisorGoal(input);
        const goals = await getAdvisorGoals(this.state.advisorInfo.userProfileId);
        this.setState({ goals: goals.data.result });
    }

    onInstSelect = async (selectedList, selectedItem) => {

        const data = { institutionId: selectedItem.id, userProfileId: this.state.advisorInfo.userProfileId };
        await assignAdvisorInstitution(data);

    }
    onInstRemove = async (selectedList, removedItem) => {


        const data = { institutionId: removedItem.id, userProfileId: this.state.advisorInfo.userProfileId };
        await unassignAdvisorInstitution(data);



    }

    render() {
        //console.log(this);
        if (!this.state.loaded) {

            return (
                <div>
                    Loading ...
                </div>

            )
        }


        return (
            <React.Fragment>
                <div className="advisorSimulateBar">
                    <button
                        type="button"
                        className="advisorSimulateButton"
                        onClick={() => this.setState({ showSimulate: true })}
                        disabled={this.state.disabled}
                    >
                        Simulate Assignment
                    </button>
                    <button
                        type="button"
                        className="advisorSimulateButton"
                        onClick={() => this.setState({ showCoverage: true })}
                        disabled={this.state.disabled}
                    >
                        Coverage Analysis
                    </button>
                </div>
                <CoverageAnalysisModal
                    open={this.state.showCoverage}
                    onClose={() => this.setState({ showCoverage: false })}
                    schoolOptions={this.props.schools}
                    districtOptions={this.state.districts}
                />
                <SimulateAssignmentModal
                    open={this.state.showSimulate}
                    onClose={() => this.setState({ showSimulate: false })}
                    userProfileId={this.state.advisorInfo.userProfileId}
                    schoolOptions={this.props.schools}
                    specialGroupOptions={this.state.activeSpecialGroups}
                    gradeOptions={gradeOptions}
                    initialSchools={this.state.schools}
                    initialGrades={this.state.grades}
                    initialZipCodes={this.state.zipCodes}
                    initialSpecialGroups={this.state.selectedSpecialGroups}
                    canApply={!this.props.readOnly && this.canApplyAssignment()}
                    onApplied={this.loadData}
                />
                <div className="objectivesSelect">
                    <br />
                    <span>Goals</span>
                    <AdvisorGoalsManager
                        taggingOptions={this.state.objTags} // Options to display in the dropdown
                        selectedValues={this.state.goals} // Preselected value to persist in dropdown
                        onSelect={this.onObjSelect} // Function will trigger on select event
                        onRemove={this.onObjRemove} // Function will trigger on remove event
                        displayValue="label" // Property name to display in the dropdown options
                        className="AdvisorObjectivesSelector"
                        disabled={this.state.disabled}
                        availableObjectives={this.state.availableObj}
                        onAdd={this.addAdvisorGoal}
                        onUpdate={this.updateAdvisorGoal}
                        onDelete={this.deleteAdvisorGoal}
                        advisorId={this.state.advisorInfo.userProfileId}
                    />

                </div>
                <span className="advisorDistrictLabel">District</span>
                <Multiselect
                    options={this.state.districts} // Districts available as a convenience selector
                    selectedValues={this.state.selectedDistricts} // Currently selected districts
                    onSelect={this.onDistrictSelect} // Auto-populates the Schools list with the district's schools
                    onRemove={this.onDistrictRemove} // Removing a district leaves assigned schools intact
                    displayValue="name" // Property name to display in the dropdown options
                    className="AdvisorSchoolSelector"
                    disabled={this.state.disabled}
                />
                <div className="password-help">
                    Selecting a district adds all of its schools below. You can still add or remove individual schools.
                </div>
                <span>Schools</span>
                <Multiselect
                    key={this.state.schoolSelectorKey} // Remount when a district programmatically populates the selection
                    options={this.props.schools} // Options to display in the dropdown
                    selectedValues={this.state.schools} // Preselected value to persist in dropdown
                    onSelect={this.onSchoolSelect} // Function will trigger on select event
                    onRemove={this.onSchoolRemove} // Function will trigger on remove event
                    displayValue="name" // Property name to display in the dropdown options
                    className="AdvisorSchoolSelector"
                    disabled={this.state.disabled}

                />

                <br />
                <span>Grades</span>
                <Multiselect
                    options={gradeOptions} // Grades 5-18 available to assign
                    selectedValues={this.state.grades} // Currently assigned grades
                    onSelect={this.onGradeSelect} // Function will trigger on select event
                    onRemove={this.onGradeRemove} // Function will trigger on remove event
                    displayValue="name" // Property name to display in the dropdown options
                    className="AdvisorSchoolSelector"
                    disabled={this.state.disabled}
                />

                <br />
                <span>Zip Codes</span>
                <div className="advisorZipCodeField">
                    <div className="advisorZipCodeChips">
                        {this.state.zipCodes.map((zip) => {
                            const removing = this.state.zipRemoving.includes(zip);
                            return (
                                <span className="advisorZipCodeChip" key={zip}>
                                    {zip}
                                    <button
                                        type="button"
                                        className="advisorZipCodeChipRemove"
                                        onClick={() => this.onZipRemove(zip)}
                                        disabled={this.state.disabled || removing}
                                        aria-label={`Remove zip code ${zip}`}
                                    >
                                        {removing ? (
                                            <span className="advisorZipCodeChipSpinner" aria-label="Removing" />
                                        ) : (
                                            <>&times;</>
                                        )}
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                    <div className="advisorZipCodeInputRow">
                        <input
                            type="text"
                            value={this.state.zipInput}
                            onChange={this.onZipInputChange}
                            onKeyDown={this.onZipKeyDown}
                            placeholder="Add zip code (e.g. 02903)"
                            maxLength={10}
                            disabled={this.state.disabled || this.state.zipAdding}
                        />
                        <button
                            type="button"
                            className="advisorZipCodeAddButton"
                            onClick={this.onZipAdd}
                            disabled={this.state.disabled || this.state.zipAdding}
                        >
                            {this.state.zipAdding ? (
                                <span className="advisorZipCodeSpinner" aria-label="Adding" />
                            ) : (
                                "Add"
                            )}
                        </button>
                    </div>
                    {this.state.zipError ? (
                        <div className="advisorZipCodeError">{this.state.zipError}</div>
                    ) : null}
                </div>

                <br />
                <span>Institutions</span>
                <Multiselect
                    options={this.props.institutions} // Options to display in the dropdown
                    selectedValues={this.state.institutions} // Preselected value to persist in dropdown
                    onSelect={this.onInstSelect} // Function will trigger on select event
                    onRemove={this.onInstRemove} // Function will trigger on remove event
                    displayValue="name" // Property name to display in the dropdown options
                    className="AdvisorSchoolSelector"
                    disabled={this.state.disabled}

                />
                <br />
                <span>Special Group Assignment</span>
                <Multiselect
                    key={this.state.specialGroupsKey} // Bump to force a remount when we need to resync chips
                    options={this.state.activeSpecialGroups} // Active special groups available to assign
                    selectedValues={this.state.selectedSpecialGroups} // Currently assigned active groups
                    onSelect={this.onSpecialGroupSelect} // Function will trigger on select event
                    onRemove={this.onSpecialGroupRemove} // Function will trigger on remove event
                    displayValue="name" // Property name to display in the dropdown options
                    className="AdvisorSchoolSelector"
                    disabled={this.state.disabled}
                />

                {(this.state.segmentOptions.length > 0 || this.state.selectedSegments.length > 0) ? (
                    <React.Fragment>
                        <br />
                        <span>Segments</span>
                        <Multiselect
                            key={this.state.segmentsKey} // Bump to force a remount when we need to resync chips after an error
                            options={this.state.segmentOptions} // All saved segments available to attach
                            selectedValues={this.state.selectedSegments} // Segments currently attached to this advisor
                            onSelect={this.onSegmentSelect} // Attaches the segment (students resolved live into the caseload)
                            onRemove={this.onSegmentRemove} // Detaches the segment
                            displayValue="name" // Property name to display in the dropdown options
                            className="AdvisorSchoolSelector"
                            disabled={this.state.disabled}
                        />
                        <div className="password-help">
                            Attaching a segment adds every current student matching its criteria to this advisor's
                            caseload (manual overrides still take precedence).
                        </div>
                    </React.Fragment>
                ) : null}

                <div >
                    <table>
                        <tr>
                            <td className="directorCheckboxContainer">
                                <FormControlLabel control={<Checkbox
                                    checked={this.state.depFamilyEngagement}
                                    disabled={this.state.disabled}
                                    onChange={this.depFamilyEngagementChanged}
                                    label="Family Engagement"

                                />} label="Family Engagement"
                                    className="directorLabel" />
                            </td>
                            <td className="directorCheckboxContainer">
                                <FormControlLabel control={<Checkbox
                                    checked={this.state.depMiddleSchool}
                                    disabled={this.state.disabled}
                                    onChange={this.depMiddleSchoolChanged}
                                    label="Middle School"

                                />} label="Middle School"
                                    className="directorLabel" />
                            </td>
                        </tr>
                        <tr>
                            <td className="directorCheckboxContainer">
                                <FormControlLabel control={<Checkbox
                                    checked={this.state.depHighSchool}
                                    disabled={this.state.disabled}
                                    onChange={this.depHighSchoolChanged}
                                    label="High School"

                                />} label="High School"
                                    className="directorLabel" />
                            </td>
                            <td className="directorCheckboxContainer">
                                <FormControlLabel control={<Checkbox
                                    checked={this.state.depPostSecondary}
                                    disabled={this.state.disabled}
                                    onChange={this.depPostSecondaryChanged}
                                    label="Post Secondary"

                                />} label="Post Secondary"
                                    className="directorLabel" />
                            </td>
                        </tr>
                    </table>


                </div>
                <br />
                <div className="directorCheckboxContainer">
                    <FormControlLabel control={<Checkbox
                        checked={this.state.showSubs}
                        disabled={this.state.disabled}
                        onChange={this.directorChanged}
                        label="Director"

                    />} label="Director"
                        className="directorLabel" />


                </div>

                {this.state.showSubs ? (
                    <div>
                        <span>Direct Reports</span>
                        <Multiselect
                            options={this.state.potSubords} // Options to display in the dropdown
                            selectedValues={this.state.subs} // Preselected value to persist in dropdown
                            onSelect={this.onSubSelect} // Function will trigger on select event
                            onRemove={this.onSubRemove} // Function will trigger on remove event
                            displayValue="displayName" // Property name to display in the dropdown options
                            className="DirectorAdvisorSelector"
                            disabled={this.state.disabled}
                        />
                    </div>
                ) : null}
                <br />
                </React.Fragment>
        );
    }
}

export default Advisor;